import { AdFarmConfig } from "./AdFarmConfig";
import { DeviceRiskPayload, MobileRole, PayoutAccountInput, RewardSessionStatus } from "./AdFarmTypes";

export class AdFarmApiError extends Error {
    constructor(message: string, readonly status: number, readonly payload: unknown) {
        super(message);
        this.name = "AdFarmApiError";
    }
}

type RequestOptions = {
    method?: string;
    body?: unknown;
    token?: string;
    timeoutMs?: number;
};

export class AdFarmApiClient {
    private token = "";
    onUnauthorized: (() => void) | null = null;
    onForbidden: ((message: string) => void) | null = null;

    constructor(readonly baseUrl = AdFarmConfig.API_BASE_URL, readonly appId = AdFarmConfig.APP_ID) {}

    setToken(token: string): void {
        this.token = token || "";
    }

    clearToken(): void {
        this.token = "";
    }

    async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
        const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
        const timer = controller ? setTimeout(() => controller.abort(), options.timeoutMs || 15000) : null;
        let response: Response;
        try {
            response = await fetch(`${this.baseUrl}${path}`, {
                method: options.method || "GET",
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json; charset=utf-8",
                    ...((options.token === undefined ? this.token : options.token) ? {
                        Authorization: `Bearer ${options.token === undefined ? this.token : options.token}`,
                    } : {}),
                },
                ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
                ...(controller ? { signal: controller.signal } : {}),
            });
        } catch (error) {
            const message = error instanceof Error && error.name === "AbortError" ? "请求超时，请稍后重试" : "网络连接失败，请检查网络后重试";
            throw new AdFarmApiError(message, 0, null);
        } finally {
            if (timer) clearTimeout(timer);
        }

        const payload = await response.json().catch(() => null) as { ok?: boolean; data?: T; error?: string; message?: string } | null;
        if (!response.ok || payload?.ok === false) {
            const message = payload?.error || payload?.message || `请求失败（${response.status}）`;
            if (response.status === 401) this.onUnauthorized?.();
            if (response.status === 403) this.onForbidden?.(message);
            throw new AdFarmApiError(message, response.status, payload);
        }
        return (payload?.data === undefined ? payload : payload.data) as T;
    }

    health<T>(): Promise<T> {
        return this.request<T>("/api/health", { token: "" });
    }

    sendCode<T>(phone: string, risk: DeviceRiskPayload): Promise<T> {
        return this.request<T>("/api/auth/send-code", { method: "POST", token: "", body: { appId: this.appId, phone, ...risk } });
    }

    register<T>(role: MobileRole, input: { phone: string; code: string; inviteCode: string; risk: DeviceRiskPayload }): Promise<T> {
        const path = role === "secondaryAgent" ? "/api/auth/register-secondary-agent" : "/api/auth/register";
        return this.request<T>(path, {
            method: "POST",
            token: "",
            body: { appId: this.appId, phone: input.phone, code: input.code, inviteCode: input.inviteCode, inviteTarget: role, ...input.risk },
        });
    }

    login<T>(input: { phone: string; code: string; role: MobileRole; risk: DeviceRiskPayload }): Promise<T> {
        return this.request<T>("/api/auth/mobile-login", {
            method: "POST",
            token: "",
            body: { appId: this.appId, phone: input.phone, code: input.code, role: input.role, ...input.risk },
        });
    }

    overview<T>(): Promise<T> {
        return this.request<T>("/api/mobile/overview");
    }

    claimQuizReward<T>(level: number): Promise<T> {
        return this.request<T>("/api/mobile/quiz-rewards", { method: "POST", body: { level } });
    }

    checkAdRisk<T>(risk: DeviceRiskPayload): Promise<T> {
        return this.request<T>("/api/mobile/ad-risk/check", { method: "POST", body: risk });
    }

    createRewardSession<T>(input: { taskId: string; sourceKey: string; transId: string; risk: DeviceRiskPayload }): Promise<T> {
        return this.request<T>("/api/mobile/reward-sessions", {
            method: "POST",
            body: { appId: this.appId, taskId: input.taskId, sourceKey: input.sourceKey, transId: input.transId, ...input.risk },
        });
    }

    reportRewardEvent<T>(transId: string, input: { provider: string; phase: string; name: string; message?: string; risk?: DeviceRiskPayload }): Promise<T> {
        return this.request<T>(`/api/mobile/reward-sessions/${encodeURIComponent(transId)}/events`, {
            method: "POST",
            body: { provider: input.provider, phase: input.phase, name: input.name, message: input.message || "", ...(input.risk || {}) },
        });
    }

    cancelRewardSession<T>(transId: string, reason: string): Promise<T> {
        return this.request<T>(`/api/mobile/reward-sessions/${encodeURIComponent(transId)}/cancel`, { method: "POST", body: { reason } });
    }

    rewardCallbackStatus(transId: string): Promise<RewardSessionStatus> {
        return this.request<RewardSessionStatus>(`/api/mobile/reward-callbacks/${encodeURIComponent(transId)}`);
    }

    savePayoutAccount<T>(role: MobileRole, input: PayoutAccountInput): Promise<T> {
        return this.request<T>(`/api/mobile/payout-account?role=${encodeURIComponent(role)}`, { method: "PATCH", body: input });
    }

    submitWithdrawal<T>(role: MobileRole): Promise<T> {
        return this.request<T>(`/api/mobile/withdrawals?role=${encodeURIComponent(role)}`, { method: "POST" });
    }
}
